<template>
  <ProvideApiFilebank v-slot="{ deleteFiles }">
    <WdsModal
      :show="isOpen"
      :title="modalTitle"
      data-test="expense-modal"
      @cancel="onCancel(deleteFiles)"
    >
      <div>
        <label class="m-t-10 f-500">{{ $t('Vendor') }} *</label>
        <input
          v-model="expenseState.vendor"
          :placeholder="$t('Enter vendor name')"
          name="vendor"
          type="text"
          class="form-control"
          data-test="expense-modal-vendor"
          :disabled="viewOnly"
        />
        <label class="m-t-20 f-500">{{ $t('Description') }}</label>
        <input
          v-model="expenseState.description"
          :placeholder="$t('Enter description')"
          name="description"
          type="text"
          class="form-control"
          data-test="expense-modal-description"
          :disabled="viewOnly"
        />
        <div class="expense-custom-fields">
          <FieldableFormCustomFields
            v-if="accountId"
            v-model:field-values="expenseState.customFieldValues"
            :account-id="accountId"
            :account-ids="customFieldProviderAccountIds"
            :fieldable-type="TypeSupportingCustomFieldValues.Expense"
            :fieldable-id="expenseState.globalId"
            should-display-errors
            :force-enable-inputs="!viewOnly"
          />
        </div>
        <div class="row m-t-20">
          <div class="col-sm-6">
            <label class="f-500">{{ $t('Expense date') }} *</label>
            <PickerDatetime
              v-model="expenseState.expenseAt"
              :inline="true"
              :as-input="true"
              :format="$options.dateFormat"
              :disabled="viewOnly"
            />
          </div>
          <div class="col-sm-6">
            <label class="f-500">
              {{ $t('Amount') }}
              {{ shouldExcludeVat ? $t('Excl. :vatTerminology', { vatTerminology }) : $t('Incl. :vatTerminology', { vatTerminology }) }} *
            </label>
            <CurrencyConversionInput
              v-model="amount"
              :disabled="viewOnly"
              :desired-currency-code="expenseReportCurrency"
              :user-currency-code="account.currency"
              :helper-input-label="`* ${account.currency}`"
            />
          </div>
        </div>
        <div v-if="shouldExcludeVat && !viewOnly" class="alert alert-info" role="alert">
          {{ $t('Please make sure to enter the amount excluding :vatTerminology', { vatTerminology }) }}
        </div>
        <div class="m-b-10">
          <label class="f-500">
            {{ $t('Files') }}
            <span v-if="filesRequired">*</span>
          </label>
          <br />
          <BaseFileAttachmentFromOs
            v-model="selectedFiles"
            :disabled="viewOnly"
            :default-tags="['Expenses']"
            @update-busy-status="handleUpdatedBusyStatus"
          />
        </div>
      </div>
      <template #footer>
        <div class="tw-flex tw-justify-between">
          <WdsButton default @click="onCancel(deleteFiles)">
            {{ viewOnly ? $t('Close') : $t('Cancel') }}
          </WdsButton>
          <WdsButton
            v-if="!viewOnly"
            primary
            data-test="confirm-expense-button"
            :disabled="saveState === 'saving' || saveState === 'saved' || isDisabled"
            @click="saveExpense"
          >
            {{ saveButtonText }}
          </WdsButton>
        </div>
      </template>
    </WdsModal>
  </ProvideApiFilebank>
</template>

<script>
import gql from 'graphql-tag'
import moment from 'moment'
import { WdsButton, WdsModal } from '@worksome/wds-components'
import BaseFileAttachmentFromOs from '@/components/BaseFileAttachmentFromOs.vue'
import CurrencyConversionInput from '@/components/CurrencyConversionInput.vue'
import PickerDatetime from '@/components/PickerDatetime.vue'
import ProvideApiFilebank from '@/components/ProvideApiFilebank.vue'
import FieldableFormCustomFields from '@/components/Fieldable/FieldableFormCustomFields.vue'
import { useFieldableFormStore } from '@/components/Fieldable/store/useFieldableFormStore'
import { mapCustomFieldValueToCustomFieldValueInput } from '@/components/Fieldable/utils'
import { TypeSupportingCustomFieldValues } from '@/gql/graphql'
import { EXPENSE_REPORT_MUTATIONS } from '@/state/mutation-types'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'

// createExpense/updateExpense live on the public API schema (client: 'publicApi'
// below), which expects global IDs rather than the local/int IDs the rest of this
// component works with (expenseReportId prop, selectedFiles from the v1-based
// upload flow). Global IDs are just base64("Type:id"), so they can be built
// client-side without an extra round trip.
function encodeGlobalId(type, id) {
  return btoa(`${type}:${id}`)
}

export default {
  components: {
    WdsButton,
    WdsModal,
    BaseFileAttachmentFromOs,
    CurrencyConversionInput,
    PickerDatetime,
    ProvideApiFilebank,
    FieldableFormCustomFields,
  },
  dateFormat: 'YYYY-MM-DD',
  props: {
    expenseReportId: { type: Number, default: 0 },
    expenseReportCurrency: { type: String, required: true },
    expense: { type: Object, default: () => ({}) },
    isOpen: { type: Boolean, default: false },
    filesRequired: { type: Boolean, default: false },
    // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
    shouldExcludeVat: { type: Boolean, default: true },
    viewOnly: { type: Boolean, default: false },
    // The company (or supplier-mirror source client) that owns the "Expenses
    // custom fields" definitions applicable to this expense's hire.
    accountId: { type: String, default: null },
    customFieldProviderAccountIds: { type: Array, default: () => [] },
  },
  emits: ['closed', 'expense-created', 'expense-updated'],
  setup() {
    return {
      TypeSupportingCustomFieldValues,
      fieldableFormStore: useFieldableFormStore(),
    }
  },
  data() {
    return {
      saveState: 'fresh',
      expenseState: this.getInitialExpenseState(),
      amount: this.getInitialAmount(),
      selectedFiles: [],
      fileComponentIsWorking: false,
    }
  },
  computed: {
    saveButtonText() {
      if (this.saveState === 'saving') {
        return $t('Saving...')
      }
      if (this.saveState === 'saved') {
        return $t('Saved')
      }
      return $t('Save')
    },
    vatTerminology() {
      return vatTerminology
    },
    persistedFiles() {
      return this.expense.hasOwnProperty('files') && this.expense.files.length
        ? this.expense.files.map((file) => ({
            localId: file.localId,
            name: file.name,
            icon: file.icon,
            storageName: file.storageName,
            link: laravelNamedRoute(namedRoutes.expenses.files.get, {
              expense: this.expense.localId,
              file: file?.storageName,
            }),
          }))
        : []
    },
    persistedFileIds() {
      return this.persistedFiles.map((file) => file.localId)
    },
    selectedFileIds() {
      return this.selectedFiles.map((file) => file.localId)
    },
    newlySelectedFileIds() {
      return this.selectedFileIds.filter(
        (selectedFileId) => !this.persistedFileIds.includes(selectedFileId),
      )
    },
    useExistingExpense() {
      return Object.entries(this.expense).length > 0
    },
    amountInCents() {
      // Round, since e.g. 19.99 * 100 is 1998.9999999999998 in JS.
      return Math.round(Number(this.amount) * 100)
    },
    isDisabled() {
      return (
        this.viewOnly ||
        !this.expenseState.vendor ||
        !this.amountInCents ||
        (this.filesRequired && this.selectedFiles.length < 1) ||
        this.fileComponentIsWorking
      )
    },
    modalId() {
      return this.useExistingExpense
        ? `expense-update-${this.expenseState.localId || ''}`
        : `expense-create-for-report-${this.expenseReportId}`
    },
    modalTitle() {
      if (this.viewOnly) {
        return $t('Expense')
      }
      return $t(this.useExistingExpense ? 'Update expense' : 'Create expense')
    },
  },
  created() {
    if (Object.entries(this.expense).length > 0) {
      this.expenseState = {
        ...this.expense,
        // Converts the stored values into the input shape FieldableFormCustomFields expects.
        customFieldValues: mapCustomFieldValueToCustomFieldValueInput(
          this.expense.customFieldValues ?? [],
        ),
      }
      this.selectedFiles = this.persistedFiles
    }
  },
  methods: {
    handleUpdatedBusyStatus(status) {
      this.fileComponentIsWorking = status
    },
    onCancel(deleteFiles) {
      this.fieldableFormStore.v$.$reset()
      this.$emit('closed')
      deleteFiles(this.newlySelectedFileIds)
      this.resetFileState()
    },
    reset() {
      this.expenseState = this.getInitialExpenseState()
      this.amount = 0
      this.selectedFiles = []
    },
    async saveExpense() {
      // Guard on the state itself, not just the disabled button: a second
      // click can still fire before Vue re-renders the disabled attribute,
      // and this check has no such timing dependency.
      if (this.saveState === 'saving') {
        return
      }

      this.saveState = 'saving'

      // The custom field form (and its shared store) is only in use when there's an accountId.
      const customFieldsAreValid = this.accountId
        ? await this.fieldableFormStore.v$.$validate()
        : true
      const expenseAtIsValid = Boolean(this.expenseState.expenseAt) && moment(this.expenseState.expenseAt).isValid()

      if (!customFieldsAreValid || !expenseAtIsValid) {
        this.saveState = 'fresh'
        return
      }

      if (Object.entries(this.expense).length > 0) {
        this.updateExpense()
      } else {
        this.createExpense()
      }
    },
    updateExpense() {
      this.saveState = 'saving'
      this.$apollo
        .mutate({
          client: 'publicApi',
          fetchPolicy: 'no-cache',
          mutation: gql`
            mutation (
              $id: ID!
              $vendor: String!
              $description: String
              $amount: Float!
              $expenseAt: Date
              $files: [ID!]
              $customFieldValues: [CustomFieldTypeValueInput!]
            ) {
              updateExpense(
                input: {
                  id: $id
                  vendor: $vendor
                  description: $description
                  amount: $amount
                  expenseAt: $expenseAt
                  files: $files
                  customFieldValues: $customFieldValues
                }
              ) {
                id
                localId: number
                vendor
                description
                amount
                currency
                expenseAt
                customFieldValues {
                  id
                  content
                  displayValue
                  customField {
                    id
                    fieldType
                    title
                  }
                  customFieldOption {
                    id
                  }
                }
              }
            }
          `,
          variables: {
            id: encodeGlobalId('Expense', this.expenseState.localId),
            vendor: this.expenseState.vendor,
            description: this.expenseState.description,
            // expenseAt is a calendar date (existing edit values may carry a full timestamp).
            expenseAt: moment(this.expenseState.expenseAt).format(this.$options.dateFormat),
            amount: this.amountInCents,
            files: this.selectedFiles.map((file) => encodeGlobalId('File', file.localId)),
            customFieldValues: this.expenseState.customFieldValues,
          },
        })
        .then((data) => {
          this.saveState = 'saved'
          // HireAndBill is watched on the default client, so refetching it through this
          // mutation's publicApi client would be a no-op.
          this.$apollo.getClient().refetchQueries({ include: ['HireAndBill'] })
          const updatedExpense = data?.data?.updateExpense
          if (updatedExpense) {
            const lineItem = {
              ...updatedExpense,
              // The API returns the UTC instant of midnight in the app's timezone (e.g. the
              // previous day at 22:00Z), so keep the calendar date that was submitted.
              expenseAt: moment(this.expenseState.expenseAt).format(this.$options.dateFormat),
              files: this.selectedFiles,
            }
            this.$store.commit(EXPENSE_REPORT_MUTATIONS.UPDATE_LINE_ITEM, {
              reportId: this.expenseReportId,
              lineItem,
            })

            this.fieldableFormStore.v$.$reset()
            this.$emit('expense-updated', lineItem)
          }
          this.$emit('closed')
        })
        .catch(() => {
          this.saveState = 'failed'
        })
        .finally(() => {
          setTimeout(() => {
            this.saveState = 'fresh'
          }, 1000)
        })
    },
    createExpense() {
      this.saveState = 'saving'
      this.$apollo
        .mutate({
          client: 'publicApi',
          fetchPolicy: 'no-cache',
          mutation: gql`
            mutation (
              $expenseReportId: ID!
              $vendor: String!
              $description: String
              $amount: Float!
              $expenseAt: Date
              $files: [ID!]
              $customFieldValues: [CustomFieldTypeValueInput!]
            ) {
              createExpense(
                input: {
                  expenseReport: $expenseReportId
                  vendor: $vendor
                  description: $description
                  amount: $amount
                  expenseAt: $expenseAt
                  files: $files
                  customFieldValues: $customFieldValues
                }
              ) {
                id
                localId: number
                vendor
                description
                amount
                currency
                expenseAt
                customFieldValues {
                  id
                  content
                  displayValue
                  customField {
                    id
                    fieldType
                    title
                  }
                  customFieldOption {
                    id
                  }
                }
              }
            }
          `,
          variables: {
            expenseReportId: encodeGlobalId('ExpenseReport', this.expenseReportId),
            vendor: this.expenseState.vendor,
            description: this.expenseState.description,
            // expenseAt is a calendar date (existing edit values may carry a full timestamp).
            expenseAt: moment(this.expenseState.expenseAt).format(this.$options.dateFormat),
            amount: this.amountInCents,
            files: this.selectedFiles.map((file) => encodeGlobalId('File', file.localId)),
            customFieldValues: this.expenseState.customFieldValues,
          },
        })
        .then((data) => {
          this.saveState = 'saved'
          // HireAndBill is watched on the default client, so refetching it through this
          // mutation's publicApi client would be a no-op.
          this.$apollo.getClient().refetchQueries({ include: ['HireAndBill'] })
          const createdExpense = data?.data?.createExpense
          if (createdExpense) {
            const lineItem = {
              ...createdExpense,
              // The API returns the UTC instant of midnight in the app's timezone (e.g. the
              // previous day at 22:00Z), so keep the calendar date that was submitted.
              expenseAt: moment(this.expenseState.expenseAt).format(this.$options.dateFormat),
              files: this.selectedFiles,
            }
            this.$store.commit(EXPENSE_REPORT_MUTATIONS.ADD_LINE_ITEM, {
              reportId: this.expenseReportId,
              lineItem,
            })

            this.fieldableFormStore.v$.$reset()
            this.$emit('expense-created', lineItem)
          }
          this.$emit('closed')
          this.reset()
        })
        .catch(() => {
          this.saveState = 'failed'
        })
        .finally(() => {
          setTimeout(() => {
            this.saveState = 'fresh'
          }, 1000)
        })
    },
    getInitialExpenseState() {
      return {
        expenseAt: moment().format(this.$options.dateFormat),
        customFieldValues: [],
      }
    },
    getInitialAmount() {
      const amount = this.expense.amount ?? 0
      return (amount / 100).toFixed(2)
    },
    resetFileState() {
      this.selectedFiles = this.selectedFiles.filter(
        (selectedFile) => !this.newlySelectedFileIds.includes(selectedFile.localId),
      )
    },
  },
}
</script>

<style scoped>
/* FieldableFormCustomFields renders one sibling block per configured field with
   no spacing of its own between them (fine when there's only one field, but
   they stack with no gap when there's more than one). Scoped to this modal
   only — the component is shared by other, WDS-only modals where this isn't
   an issue. */
.expense-custom-fields {
  margin-top: 20px;
}
.expense-custom-fields :deep(> * + *) {
  margin-top: 20px;
}
</style>
